import { Centre, Country, School } from "app/shared/model";
import {
  CLEAR_SELECTED_SCHOOL,
  FETCH_CENTRES_FAILURE,
  FETCH_CENTRES_REQUEST,
  FETCH_CENTRES_SUCCESS,
  FETCH_COUNTRIES_FAILURE,
  FETCH_COUNTRIES_REQUEST,
  FETCH_COUNTRIES_SUCCESS,
  FETCH_SCHOOL_DETAIL_FAILURE,
  FETCH_SCHOOL_DETAIL_REQUEST,
  FETCH_SCHOOL_DETAIL_SUCCESS,
  FETCH_SCHOOLS_FAILURE,
  FETCH_SCHOOLS_REQUEST,
  FETCH_SCHOOLS_SUCCESS,
  SAVE_SCHOOL_FAILURE,
  SAVE_SCHOOL_REQUEST,
  SAVE_SCHOOL_SUCCESS,
  SET_FORM_MODE_CREATE,
  SET_FORM_MODE_EDIT,
  SET_SELECTED_CENTRE,
  SET_SELECTED_SCHOOL,
  SchoolManagerAction,
  normalizeSchoolId,
  toSchoolCacheKey,
} from "./actions";

export type FormMode = "create" | "edit";

export interface AsyncState<T> {
  data: T;
  loading: boolean;
  error?: string;
}

export interface SchoolManagerState {
  centres: AsyncState<Centre[]>;
  countries: AsyncState<Country[]>;
  selectedCentreId: string | number | null;
  schools: AsyncState<School[]>;
  selectedSchoolId: string | number | null;
  schoolDetails: Record<string | number, School>;
  detailLoadingId: string | number | null;
  detailError?: string;
  saving: boolean;
  saveError?: string;
  formMode: FormMode;
}

const initialAsyncState = <T>(data: T): AsyncState<T> => ({ data, loading: false, error: undefined });

export const initialState: SchoolManagerState = {
  centres: initialAsyncState<Centre[]>([]),
  countries: initialAsyncState<Country[]>([]),
  selectedCentreId: null,
  schools: initialAsyncState<School[]>([]),
  selectedSchoolId: null,
  schoolDetails: {},
  detailLoadingId: null,
  detailError: undefined,
  saving: false,
  saveError: undefined,
  formMode: "create",
};

export default function reducer(
  state: SchoolManagerState = initialState,
  action: SchoolManagerAction
): SchoolManagerState {
  switch (action.type) {
    case FETCH_CENTRES_REQUEST:
      return {
        ...state,
        centres: { ...state.centres, loading: true, error: undefined },
      };

    case FETCH_CENTRES_SUCCESS:
      return {
        ...state,
        centres: { data: action.payload ?? [], loading: false, error: undefined },
      };

    case FETCH_CENTRES_FAILURE:
      return {
        ...state,
        centres: { ...state.centres, loading: false, error: action.error },
      };

    case FETCH_COUNTRIES_REQUEST:
      return {
        ...state,
        countries: { ...state.countries, loading: true, error: undefined },
      };

    case FETCH_COUNTRIES_SUCCESS:
      return {
        ...state,
        countries: { data: action.payload ?? [], loading: false, error: undefined },
      };

    case FETCH_COUNTRIES_FAILURE:
      return {
        ...state,
        countries: { ...state.countries, loading: false, error: action.error },
      };

    case SET_SELECTED_CENTRE:
      return {
        ...state,
        selectedCentreId: action.payload ?? null,
        selectedSchoolId: null,
        formMode: "create",
        detailLoadingId: null,
        detailError: undefined,
        saveError: undefined,
      };

    case FETCH_SCHOOLS_REQUEST:
      return {
        ...state,
        schools: { ...state.schools, loading: true, error: undefined },
      };

    case FETCH_SCHOOLS_SUCCESS:
      return {
        ...state,
        schools: { data: action.payload ?? [], loading: false, error: undefined },
      };

    case FETCH_SCHOOLS_FAILURE:
      return {
        ...state,
        schools: { ...state.schools, loading: false, error: action.error },
      };

    case SET_SELECTED_SCHOOL: {
      const key = toSchoolCacheKey(action.payload) ?? (action.payload != null ? String(action.payload) : null);
      return {
        ...state,
        selectedSchoolId: key,
        saveError: undefined,
      };
    }

    case CLEAR_SELECTED_SCHOOL:
      return {
        ...state,
        selectedSchoolId: null,
        formMode: "create",
        saveError: undefined,
      };

    case SET_FORM_MODE_CREATE:
      return {
        ...state,
        formMode: "create",
      };

    case SET_FORM_MODE_EDIT:
      return {
        ...state,
        formMode: "edit",
      };

    case FETCH_SCHOOL_DETAIL_REQUEST: {
      const key = toSchoolCacheKey(action.payload) ?? (action.payload != null ? String(action.payload) : null);
      return {
        ...state,
        detailLoadingId: key,
        detailError: undefined,
        saveError: undefined,
      };
    }

    case FETCH_SCHOOL_DETAIL_SUCCESS: {
      const school = action.payload;
      const normalizedId = normalizeSchoolId(school?.id);
      const key = normalizedId ?? (school?.id != null ? String(school.id) : null);
      if (!key) {
        return {
          ...state,
          detailLoadingId: null,
          detailError: undefined,
        };
      }
      const normalizedSchool: School = {
        ...school,
        id: normalizedId ?? school.id,
      };
      return {
        ...state,
        detailLoadingId: null,
        detailError: undefined,
       selectedSchoolId: key,
        schoolDetails: {
          ...state.schoolDetails,
          [key]: normalizedSchool,
        },
      };
    }


    case FETCH_SCHOOL_DETAIL_FAILURE:
      return {
        ...state,
        detailLoadingId: null,
        detailError: action.payload?.error,
      };

    case SAVE_SCHOOL_REQUEST:
      return {
        ...state,
        saving: true,
        saveError: undefined,
      };

    case SAVE_SCHOOL_SUCCESS: {
      const nextSelectedRaw = action.payload?.newId ?? state.selectedSchoolId;
      const normalizedSelectedId = nextSelectedRaw != null
        ? toSchoolCacheKey(nextSelectedRaw) ?? String(nextSelectedRaw)
        : null;
      const maybeSchool = action.payload?.school ?? null;
      const detailMap = { ...state.schoolDetails };
      if (maybeSchool) {
        const normalizedId = normalizeSchoolId(maybeSchool.id);
        const key = normalizedId ?? (maybeSchool.id != null ? String(maybeSchool.id) : normalizedSelectedId);
        if (key) {
          detailMap[key] = {
            ...maybeSchool,
            id: normalizedId ?? maybeSchool.id,
          };
        }
      }

      const nextSchools = (() => {
        if (!maybeSchool) return state.schools;
        const key = normalizeSchoolId(maybeSchool.id) ?? (maybeSchool.id != null ? String(maybeSchool.id) : null);
        if (!key) return state.schools;
        const current = state.schools?.data ?? [];
        const idx = current.findIndex((s) => {
          const sid = normalizeSchoolId((s as any)?.id) ?? ((s as any)?.id != null ? String((s as any).id) : null);
          return sid != null && sid === key;
        });
        if (idx === -1) {
          return { ...state.schools, data: [maybeSchool as any, ...current] };
        }
        const updated = [...current];
        updated[idx] = { ...(current[idx] as any), ...(maybeSchool as any), id: normalizeSchoolId((maybeSchool as any).id) ?? (maybeSchool as any).id };
        return { ...state.schools, data: updated };
      })();
      return {
        ...state,
        saving: false,
        saveError: undefined,
        selectedSchoolId: normalizedSelectedId ?? null,
        schoolDetails: detailMap,
        schools: nextSchools,
      };
    }

    case SAVE_SCHOOL_FAILURE:
      return {
        ...state,
        saving: false,
        saveError: action.error,
      };

    default:
      return state;
  }
}